/**
 * PostHog analytics — PII-safe wrapper.
 *
 * Tracks 4 key funnel events:
 *   - user_signed_up
 *   - checkout_started
 *   - checkout_completed
 *   - order_fulfilled  (fired from edge function, not here)
 *
 * Constraints:
 *   - No raw emails / names / phones in event properties.
 *   - Respects Do-Not-Track and cookie consent (best-effort).
 *   - Safe no-op if key is missing or SDK fails to load.
 */
import posthog from "posthog-js";
import { isAllowed } from "@/lib/consent/manager";


const env = import.meta.env as Record<string, string | undefined>;

/**
 * Key/host resolution order:
 *   1. VITE_PUBLIC_POSTHOG_KEY / VITE_PUBLIC_POSTHOG_HOST (explicit project config)
 *   2. Lovable PostHog connector-synced vars (key + region)
 * Missing config = analytics silently disabled, app unaffected.
 */
const POSTHOG_KEY =
  env.VITE_PUBLIC_POSTHOG_KEY || env.VITE_LOVABLE_CONNECTOR_POSTHOG_API_KEY;

const POSTHOG_HOST =
  env.VITE_PUBLIC_POSTHOG_HOST ||
  ((env.VITE_LOVABLE_CONNECTOR_POSTHOG_REGION || "us") === "eu"
    ? "https://eu.i.posthog.com"
    : "https://us.i.posthog.com");

let initialized = false;

/** True once the SDK is live and safe to call. */
export function isPostHogReady() {
  return initialized;
}

/** Raw client accessor — null when analytics is disabled/unconfigured. */
export function getPostHog(): typeof posthog | null {
  return initialized ? posthog : null;
}

export function initPostHog() {
  if (initialized || typeof window === "undefined") return;
  if (!POSTHOG_KEY) return;
  // Defense in depth: never boot without an explicit analytics grant.
  if (!isAllowed("analytics")) return;
  // Guard against React StrictMode double-invocation / duplicate loaders.
  if ((window as unknown as { __astrixyPostHogInit?: boolean }).__astrixyPostHogInit) return;
  try {
    // Skip in Lovable preview iframes to avoid noisy dev events.
    const host = window.location.hostname;
    if (host.includes("id-preview--") || host.includes("lovableproject")) return;

    (window as unknown as { __astrixyPostHogInit?: boolean }).__astrixyPostHogInit = true;

    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: "identified_only",
      capture_pageview: true, // SPA history changes handled by the SDK
      capture_pageleave: true,
      autocapture: false, // we send explicit, named events only
      respect_dnt: true,
      // ── Session replay: on, but privacy-first ──────────────
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "[data-ph-mask], [data-sensitive], .ph-mask",
        blockSelector: "[data-ph-block], iframe[src*='stripe'], iframe[src*='paypal'], .StripeElement",
      },
      loaded: () => {
        initialized = true;
        window.dispatchEvent(new CustomEvent("astrixy-posthog-ready"));
      },
    });
    initialized = true;
    window.dispatchEvent(new CustomEvent("astrixy-posthog-ready"));
  } catch (e) {
    console.warn("[posthog] init failed:", e);
  }
}


/** Identify user by UUID only — never email. */
export function identifyUser(userId: string) {
  if (!initialized) return;
  try {
    posthog.identify(userId);
  } catch (e) {
    console.warn("[posthog] identify failed:", e);
  }
}

export function resetUser() {
  if (!initialized) return;
  try {
    posthog.reset();
  } catch {
    /* noop */
  }
}

export function track(event: string, props?: Record<string, unknown>) {
  if (!initialized) return;
  try {
    posthog.capture(event, props);
  } catch (e) {
    console.warn(`[posthog] track ${event} failed:`, e);
  }
}

/**
 * Auth funnel tracking — PII-safe.
 * Login: login_page_viewed → login_form_started → login_submitted → (login_failed | login_succeeded)
 * Signup: signup_page_viewed → signup_form_started → signup_submitted → (signup_failed | email_otp_sent → email_otp_verified → signup_completed)
 * Social: social_login_clicked → (social_login_failed | social_login_succeeded)
 * NEVER pass raw email — pass email_domain (after @) only.
 */
export type AuthFunnelEvent =
  | "login_page_viewed"
  | "login_form_started"
  | "login_submitted"
  | "login_failed"
  | "login_succeeded"
  | "signup_page_viewed"
  | "signup_form_started"
  | "signup_submitted"
  | "signup_failed"
  | "email_otp_sent"
  | "email_otp_verified"
  | "email_otp_failed"
  | "signup_completed"
  | "social_login_clicked"
  | "social_login_succeeded"
  | "social_login_failed";

export function trackAuthFunnel(
  event: AuthFunnelEvent,
  props?: { method?: string; reason?: string; provider?: string; email_domain?: string; [k: string]: unknown },
) {
  track(event, { funnel: "auth", ...props });
}

/** Extract domain portion of email for funnel segmentation. Never logs the local part. */
export function emailDomain(email?: string | null): string | undefined {
  if (!email) return undefined;
  const at = email.lastIndexOf("@");
  return at > 0 ? email.slice(at + 1).toLowerCase() : undefined;
}
